/**
 * Bank Account API Transform Functions
 * Handles snake_case (backend) <-> camelCase (frontend) conversion
 */

import type {
  BankAccountResponse,
  BankAccountListResponse,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@/types/api/bank';

/**
 * Raw bank account response from backend (mixed case - backend returns mixed camelCase/snake_case)
 * Some fields are camelCase: bankId, isDefault, accountName, bankName, branchName, currency, accountNumber, swiftCode, iban, notes, isActive
 * Some fields are snake_case: country_id, country_name_en, country_name_zh
 */
interface RawBankAccountResponse {
  // camelCase fields from backend
  bankId?: number;
  isDefault?: boolean;
  accountName?: string;
  bankName?: string;
  bankCode?: string;
  branchName?: string | null;
  branchCode?: string;
  currency?: string;
  accountNumber?: string;
  swiftCode?: string | null;
  iban?: string | null;
  notes?: string | null;
  isActive?: boolean;
  // snake_case fields from backend
  bank_id?: number;
  is_default?: boolean;
  account_name?: string;
  country_id?: number;
  countryId?: number; // Sometimes backend returns camelCase
  country_name_en?: string;
  country_name_zh?: string;
  countryNameEn?: string; // Sometimes backend returns camelCase
  countryNameZh?: string; // Sometimes backend returns camelCase
  bank_name?: string;
  bank_code?: string;
  branch_name?: string | null;
  branch_code?: string;
  account_number?: string;
  swift_code?: string | null;
  is_active?: boolean;
}

/**
 * Raw list response from backend
 */
interface RawBankAccountListResponse {
  data: RawBankAccountResponse[];
  pagination: {
    page: number;
    pageSize?: number; // Backend might use pageSize or limit
    limit?: number; // Some backends use limit instead
    total: number;
    totalPages: number;
  };
}

/**
 * Transform single bank account response to camelCase
 * Handles mixed case responses from backend (some fields camelCase, some snake_case)
 */
export const transformBankAccountResponse = (
  raw: RawBankAccountResponse
): BankAccountResponse => {
  return {
    // Use camelCase first, fallback to snake_case
    bankId: raw.bankId ?? raw.bank_id ?? 0,
    isDefault: raw.isDefault ?? raw.is_default ?? false,
    accountName: raw.accountName ?? raw.account_name ?? '',
    countryId: raw.countryId ?? raw.country_id ?? 0,
    countryNameEn: raw.countryNameEn ?? raw.country_name_en,
    countryNameZh: raw.countryNameZh ?? raw.country_name_zh,
    bankName: raw.bankName ?? raw.bank_name ?? '',
    bankCode: raw.bankCode ?? raw.bank_code,
    branchName: raw.branchName ?? raw.branch_name ?? null,
    branchCode: raw.branchCode ?? raw.branch_code,
    currency: raw.currency ?? '',
    accountNumber: raw.accountNumber ?? raw.account_number ?? '',
    swiftCode: raw.swiftCode ?? raw.swift_code ?? null,
    iban: raw.iban ?? null,
    notes: raw.notes ?? null,
    isActive: raw.isActive ?? raw.is_active ?? true,
  };
};

/**
 * Transform bank account list response from snake_case to camelCase
 */
export const transformBankAccountListResponse = (
  raw: RawBankAccountListResponse
): BankAccountListResponse => {
  return {
    data: raw.data.map(transformBankAccountResponse),
    pagination: {
      page: raw.pagination.page,
      pageSize: raw.pagination.pageSize ?? raw.pagination.limit ?? 10,
      total: raw.pagination.total,
      totalPages: raw.pagination.totalPages,
    },
  };
};

/**
 * Transform bank account request for backend
 * Backend expects camelCase for all fields
 */
export const transformBankAccountRequest = (
  data: CreateBankAccountRequest | UpdateBankAccountRequest
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if ('vendorId' in data && data.vendorId !== undefined) {
    result.vendorId = data.vendorId;
  }
  if (data.isDefault !== undefined) {
    result.isDefault = data.isDefault;
  }
  if (data.accountName !== undefined) {
    result.accountName = data.accountName;
  }
  if (data.countryId !== undefined) {
    result.countryId = data.countryId;
  }
  if ('countryNameEn' in data && data.countryNameEn !== undefined) {
    result.countryNameEn = data.countryNameEn;
  }
  if ('countryNameZh' in data && data.countryNameZh !== undefined) {
    result.countryNameZh = data.countryNameZh;
  }
  if (data.bankName !== undefined) {
    result.bankName = data.bankName;
  }
  if ('bankCode' in data && data.bankCode !== undefined) {
    result.bankCode = data.bankCode;
  }
  if (data.branchName !== undefined) {
    result.branchName = data.branchName;
  }
  if ('branchCode' in data && data.branchCode !== undefined) {
    result.branchCode = data.branchCode;
  }
  if (data.currency !== undefined) {
    result.currency = data.currency;
  }
  if (data.accountNumber !== undefined) {
    result.accountNumber = data.accountNumber;
  }
  if (data.swiftCode !== undefined) {
    result.swiftCode = data.swiftCode;
  }
  if (data.iban !== undefined) {
    result.iban = data.iban;
  }
  if (data.notes !== undefined) {
    result.notes = data.notes;
  }
  if (data.isActive !== undefined) {
    result.isActive = data.isActive;
  }

  return result;
};
